import { IssuesModule } from '@/core/modules/issues'
import { useCallback, useMemo, useState } from 'react'
import { getSnakeCaseTodayDate } from '../utils/utils'
import { pdf } from '@react-pdf/renderer'
import { PdfIssues } from '../index'

type ExportType = 'all' | 'filtered'

export const useIssuesExport = (issues: TIssue[], filteredIssues: TIssue[]) => {
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<ExportType | null>(null)

    const date = useMemo(() => getSnakeCaseTodayDate(), [])
    const isFilterActive = useMemo(
        () => IssuesModule.filterModule.hasActiveFilters(),
        [IssuesModule.filterModule]
    )

    const getFilterInfo = useCallback(() => {
        const activeFilters = IssuesModule.filterModule.getActiveFilters()
        const filterState = IssuesModule.filterModule.store.get().filter

        return {
            filterNames: activeFilters.map((filter) => filter.filterName),
            statuses: Object.keys(filterState.status || {}).filter(
                (key) => filterState.status[key]
            ) as TIssueStatus[],
        }
    }, [])

    const downloadPdf = useCallback(
        async (type: ExportType) => {
            const isFiltered = type === 'filtered'
            const targetIssues = isFiltered ? filteredIssues : issues
            const fileName = isFiltered
                ? `Stance_filtered_issues_${date}.pdf`
                : `Stance_issues_${date}.pdf`

            if (!targetIssues.length || (isFiltered && !isFilterActive)) {
                return
            }

            setLoading(type)
            setError(null)

            try {
                const { filterNames, statuses } = isFiltered
                    ? getFilterInfo()
                    : { filterNames: [], statuses: [] }

                const doc = (
                    <PdfIssues
                        issues={targetIssues}
                        onDocUpdated={() => {}}
                        isFiltered={isFiltered}
                        filteredTypes={filterNames}
                        statuses={statuses}
                    />
                )

                // Allow UI to update before heavy work
                await new Promise((resolve) => setTimeout(resolve, 0))

                const blob = await pdf(doc).toBlob()
                const url = URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
            } catch (err) {
                console.error('PDF generation failed:', err)
                setError(
                    err instanceof Error ? err.message : 'PDF generation failed'
                )
            } finally {
                setLoading(null)
            }
        },
        [issues, filteredIssues, isFilterActive, date, getFilterInfo]
    )

    const downloadAll = useCallback(() => downloadPdf('all'), [downloadPdf])

    const downloadFiltered = useCallback(
        () => downloadPdf('filtered'),
        [downloadPdf]
    )

    return {
        error,
        isFilterActive,
        loadingAll: loading === 'all',
        loadingFiltered: loading === 'filtered',
        disabledAll: !issues.length,
        disabledFiltered: !(isFilterActive && filteredIssues.length > 0),
        downloadAll,
        downloadFiltered,
    }
}
